import { PayloadAction } from '@reduxjs/toolkit';
import { RTCEventPayload } from 'features/streaming/types';
import { NO_PUBLISHING_FEED } from 'utils/webrtc/errors';
import { call } from 'redux-saga/effects';
import { PublishingRoomJoinedMessage, RTCClient } from 'utils/webrtc';
import { PublishingFeed } from 'utils/webrtc/publishing/PublishingFeed';
import { ScreensharingFeed } from 'utils/webrtc/publishing/ScreensharingFeed';
import {
  emitPublishEvent,
  PUBLISH_DEFERRED_RESOLVED_EVENT,
  publishDeferredResolvedMessage,
} from 'utils/webrtc/publishing/publishTelemetry';

// @TODO refine payload type
export function* onPublisherJoinedSaga(
  action: PayloadAction<RTCEventPayload<PublishingRoomJoinedMessage>>
) {
  const { handle, message, kind } = action.payload;
  if (!kind) {
    throw NO_PUBLISHING_FEED(handle);
  }

  const feed: PublishingFeed | ScreensharingFeed = yield call(RTCClient.getPublishingFeed, kind);
  const { handle: videoroom } = feed;

  // Cleared unconditionally, even on the throw-below path: a deferral that reaches it is dead —
  // the publish it was waiting for cannot happen on this join — and a surviving latch would let
  // a later, unrelated join report a poisoned msSinceDefer against it.
  const { publishDeferredAt } = feed;
  feed.publishDeferredAt = null;

  if (videoroom) {
    videoroom.privateId = message.private_id;
    videoroom.feedId = message.id;

    yield call(RTCClient.setupLocalFeed, message);

    // This saga runs on every join, but a defer only happened on some of them —
    // pair the resolution to the latch so the funnel gap stays meaningful.
    if (publishDeferredAt !== null) {
      emitPublishEvent({
        event: PUBLISH_DEFERRED_RESOLVED_EVENT,
        message: publishDeferredResolvedMessage('no-handle'),
        level: 'info',
        feedType: kind,
        visibility: 'fleet',
        cap: 10,
        attributes: {
          reason: 'no-handle',
          msSinceDefer: performance.now() - publishDeferredAt,
        },
      });
    }

    yield call(feed.requestPublish);

    yield call(RTCClient.receivingFeed.updateSimulcastConfig);
  } else {
    throw NO_PUBLISHING_FEED(handle);
  }
}
